import React, { useState, useEffect } from 'react';
import Routes from './Routes';
import './App.scss';
import { AppContext } from './libs/contextLib';
import { updateErrorsArray, clearNonPersistErrors, UiError } from './libs/errorLib';
import { userStates } from './libs/userLib';
import { RedirectInfo } from './libs/redirectInfo';
import IeWarning from './components/IeWarning';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import AuthManager from './components/AuthManager';
import LoadPage from './components/LoadPage';
import { Container, Card } from 'react-bootstrap';
import { BrowserRouter as Router } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { MatomoProvider, createInstance } from '@jonkoops/matomo-tracker-react'
import config from './config';
// Notificaitons
import Notifications from './components/Notifications';
// Errors.
import ErrorBar from './components/ErrorBar';

// Configure Amplify.
Amplify.configure(
  {
    Auth: {
      mandatorySignIn: true,
      region: config.cognito.REGION,
      userPoolId: config.cognito.USER_POOL_ID,
      userPoolWebClientId: config.cognito.APP_CLIENT_ID,
      oauth: config.oauth
    }
  }
);

// Configure matomo
const matomoInstance = createInstance({
  urlBase: config.analytics.url,
  siteId: config.analytics.siteId,
  disabled: false, // optional, false by default. Makes all tracking calls no-ops if set to true.
  heartBeat: { // optional, enabled by default
    active: true, // optional, default value: true
    seconds: 10 // optional, default value: `15
  },
  linkTracking: true, // We'll handle link tracking.
  configurations: {
    requireConsent: true,
    requireCookieConsent: true,
  },
})

/**
 * Main applicaiton component.
 */
function App() {
  // These are some global states. They are passed to the context provider.
  const [userState, setUserState]             = useState(userStates.INITIALISING);
  const [user, setUser]                       = useState(null)
  const [userType, setUserType]               = useState(0);
  const [userIsFederated, setUserIsFederated] = useState(null);
  const [providerName, setProviderName]       = useState('');
  const [userDepartment, setUserDepartment]   = useState('');
  const [errors, setErrors]                   = useState([]);
  const [redirectInfo, setRedirectInfo]       = useState(new RedirectInfo());
  // Display a message to the user when we're redirecting.
  const [authRedirecting, setAuthRedirecting] = useState(false);

  useEffect(() => {
    // Remove the selector used to set the body style to be suitable for the
    // pre-loader.
    let loader = document.querySelector('#initialLoader');
    if(loader) {
      loader.removeAttribute('id');
    }
  });

  /**
   * Custom setter for the errors array.
   * @param {UiError} uiError 
   *  UiError to process.
   */
  const setError = (uiError) => {
    // Convert to proper UiError if it's a standard object.
    if (typeof uiError != UiError) {
      if (
        uiError.hasOwnProperty('key') &&
        uiError.hasOwnProperty('message') &&
        uiError.hasOwnProperty('severity')
      ) {
        uiError = new UiError(uiError.key, uiError.message, uiError.severity);
      }
      else {
        console.error(['Invalid type error in setError', uiError]);
        return;
      }
    }
    // We have to use the spread operator here or no rerender will be triggered.
    // I'm not sure why?
    setErrors([...updateErrorsArray(uiError, errors)]);
  }

  /**
   * Clears ui errors.
   */
  const clearErrors = () => {
    setErrors([...clearNonPersistErrors(errors)]);
    //setErrors([]);
  }

  function getPageContent() {
    // Are we initialising?
    if (userState === userStates.INITIALISING) {
      return (<LoadPage light={ true }/>);
    }
    // redirecting after authenticating?
    else if (authRedirecting) {
      return (
        <>
          <Navigation/>
          <Notifications/>
          <Container fluid="sm">
            <Card border = "primary">
              <Card.Body>
                <Card.Text>
                  You have been authenticated, and will be redirected shortly.
                </Card.Text>
              </Card.Body>
            </Card>
          </Container>
          <div className="wrapper flex-grow-1">
          </div>
          <Footer/>
        </>
      );
    }
    // Or just showing the page.
    else {
      return(
        <>
          <Navigation/>
          <IeWarning/>
          <ErrorBar/>
          <Notifications/>
          <Routes/>
          <div className="wrapper flex-grow-1">
          </div>
          <Footer/>
        </>
      );
    }
  }

  return (
    <MatomoProvider value={matomoInstance}>
      <Router>
        <div className="d-flex flex-column min-vh-100">
          <AppContext.Provider value={{
            userState, setUserState,
            user, setUser,
            userType, setUserType,
            userIsFederated, setUserIsFederated,
            providerName, setProviderName,
            userDepartment, setUserDepartment,
            errors, setError, clearErrors,
            redirectInfo, setRedirectInfo,
            setAuthRedirecting
          }}>
            <AuthManager />
            { getPageContent() }
          </AppContext.Provider>
        </div>
      </Router>
    </MatomoProvider>
  );
}

export default App;
